import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';
import { translate as t } from 'hooks/useTranslations';
import { DndContext, closestCenter } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { neutral, primary } from '_lex/_foundations/phxColors';
import ContentEditor from 'components/ContentEditor';
import ComplianceChecklistItem from './ComplianceChecklistItem';

export interface ChecklistItem {
  id: string;
  completed?: boolean;
  enabled: boolean;
  draftContent?: string;
  publishedContent?: string;
  position: number;
  organizationId?: string;
}

export interface ComplianceChecklistProps {
  items: ChecklistItem[];
  isLoading: boolean;
  isCreatingItem: boolean;
  isUpdatingItem: boolean;
  isDeletingItem?: boolean;
  isReordering?: boolean;
  onSaveEdit: (id: string, html: string) => Promise<unknown>;
  onToggleCompleted?: (id: string, completed: boolean) => Promise<unknown>;
  onToggleEnabled: (id: string, enabled: boolean) => Promise<unknown>;
  onSaveNewItem: (html: string, position: number) => Promise<unknown>;
  onDelete?: (id: string) => Promise<unknown>;
  onReorder?: (items: { id: string; position: number }[]) => Promise<unknown>;
  deleteDisabled?: boolean;
  deleteDisabledTooltip?: string;
  currentOrganizationId?: string;
}

const ComplianceChecklist: React.FC<ComplianceChecklistProps> = ({
  items,
  isLoading,
  isCreatingItem,
  isUpdatingItem,
  isDeletingItem = false,
  isReordering = false,
  onSaveEdit,
  onToggleCompleted,
  onToggleEnabled,
  onSaveNewItem,
  onDelete,
  onReorder,
  deleteDisabled = false,
  deleteDisabledTooltip,
  currentOrganizationId,
}) => {
  const confirm = useConfirm();
  const [orderedItems, setOrderedItems] = useState<ChecklistItem[]>([]);
  useEffect(() => {
    setOrderedItems((prev) => {
      const next = [...items].sort((a, b) => a.position - b.position);
      const sameRow = (a: ChecklistItem, b: ChecklistItem) =>
        a.id === b.id &&
        a.position === b.position &&
        (a.completed ?? false) === (b.completed ?? false) &&
        a.enabled === b.enabled &&
        (a.draftContent ?? '') === (b.draftContent ?? '') &&
        (a.publishedContent ?? '') === (b.publishedContent ?? '');
      const unchanged =
        next.length === prev.length && next.every((item, i) => prev[i] !== undefined && sameRow(item, prev[i]!));
      return unchanged ? prev : next;
    });
  }, [items]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [newItemDraft, setNewItemDraft] = useState<string | null>(null);
  const [newItemHTML, setNewItemHTML] = useState<string>('');

  const busy = isCreatingItem || isUpdatingItem || isDeletingItem || isReordering;

  const cancelEdit = () => setEditingId(null);

  const handleSaveEdit = (contentHTML: string) => {
    if (!editingId) return;
    onSaveEdit(editingId, contentHTML)
      .then(() => setEditingId(null))
      .catch(() => {});
  };

  const handleToggleCompleted = (item: ChecklistItem) => {
    onToggleCompleted?.(item.id, !(item.completed ?? false)).catch(() => {});
  };

  const handleToggleEnabled = (item: ChecklistItem) => {
    onToggleEnabled(item.id, !item.enabled).catch(() => {});
  };

  const handleDelete = (item: ChecklistItem) => {
    if (!onDelete || deleteDisabled) return;
    confirm({
      title: t('awb.complianceChecklist.lexipolTemplate.removeModal.title'),
      description: t('awb.complianceChecklist.lexipolTemplate.removeModal.description'),
      cancellationButtonProps: { variant: 'outlined', color: 'neutral' },
      confirmationButtonProps: { variant: 'contained', color: 'error' },
      confirmationText: t('awb.complianceChecklist.lexipolTemplate.removeModal.confirm'),
      cancellationText: t('awb.complianceChecklist.lexipolTemplate.removeModal.cancel'),
    })
      .then(() =>
        onDelete(item.id).then(() => {
          if (editingId === item.id) cancelEdit();
        })
      )
      .catch(() => {});
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!onReorder) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = orderedItems.findIndex((i) => i.id === active.id);
    const newIdx = orderedItems.findIndex((i) => i.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(orderedItems, oldIdx, newIdx);
    setOrderedItems(reordered);
    onReorder(reordered.map((item, pos) => ({ id: item.id, position: pos + 1 })))
      .catch(() => {
        setOrderedItems([...items].sort((a, b) => a.position - b.position));
      });
  };

  const handleAddItem = () => {
    setNewItemDraft('');
    setNewItemHTML('');
    setEditingId(null);
  };

  const isNewItemEmpty = !newItemHTML || !newItemHTML.replace(/<[^>]*>/g, '').trim();

  const handleSaveNewItem = () => {
    const maxPosition = orderedItems.length > 0 ? Math.max(...orderedItems.map((i) => i.position)) : 0;
    onSaveNewItem(newItemHTML, maxPosition + 1)
      .then(() => {
        setNewItemDraft(null);
        setNewItemHTML('');
      })
      .catch(() => {});
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 3, px: 2 }}>
        <Typography color='text.secondary'>{t('awb.complianceChecklist.lexipolTemplate.loading')}</Typography>
      </Box>
    );
  }

  if (orderedItems.length === 0 && newItemDraft === null) {
    return (
      <Box sx={{ py: 4, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minHeight: 280 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, py: 4 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: neutral[50],
              border: `1px solid ${neutral[100]}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Add sx={{ color: primary[800], fontSize: 28 }} />
          </Box>
          <Typography variant='subtitleMd' sx={{ fontWeight: 500, color: neutral[800], textAlign: 'center' }}>
            {t('awb.complianceChecklist.lexipolTemplate.emptyTitle')}
          </Typography>
          <Typography variant='bodyMd' color='text.secondary' sx={{ textAlign: 'center', maxWidth: 400 }}>
            {t('awb.complianceChecklist.lexipolTemplate.emptyHint')}
          </Typography>
        </Box>
        <Stack direction='row' justifyContent='flex-end' sx={{ width: '100%' }}>
          <Button variant='outlined' color='neutral' size='medium' startIcon={<Add />} onClick={handleAddItem} disabled={busy}>
            {t('awb.complianceChecklist.lexipolTemplate.addItem')}
          </Button>
        </Stack>
      </Box>
    );
  }

  const disabledCount = orderedItems.filter((i) => !i.enabled).length;

  return (
    <Box sx={{ py: 2, px: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {disabledCount > 0 && (
        <Typography variant='caption' color='text.secondary' sx={{ px: 1 }}>
          {t('awb.complianceChecklist.lexipolTemplate.itemsSummary', '', {
            total: String(orderedItems.length),
            disabled: String(disabledCount),
          })}
        </Typography>
      )}

      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={orderedItems.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <Stack spacing={0}>
            {orderedItems.map((item) => (
              <ComplianceChecklistItem
                key={item.id}
                id={item.id}
                content={item.draftContent ?? item.publishedContent ?? ''}
                completed={item.completed}
                enabled={item.enabled}
                canEditContent={currentOrganizationId ? item.organizationId === currentOrganizationId : true}
                isEditing={editingId === item.id}
                onStartEdit={() => setEditingId(item.id)}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={cancelEdit}
                onToggleCompleted={onToggleCompleted ? () => handleToggleCompleted(item) : undefined}
                onToggleEnabled={() => handleToggleEnabled(item)}
                onDelete={onDelete ? () => handleDelete(item) : undefined}
                deleteDisabled={deleteDisabled}
                deleteDisabledTooltip={deleteDisabledTooltip}
                busy={busy}
                isSaving={isUpdatingItem}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>

      {newItemDraft !== null ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              border: `1px solid ${primary[800]}`,
              borderRadius: 1,
              bgcolor: 'background.paper',
              mb: 0.5,
            }}
          >
            <ContentEditor
              id='compliance-item-new'
              contentHTML=''
              isEditable
              variant='minimal'
              onSave={setNewItemHTML}
              trackChangesEnabled={false}
              textEntitiesEnabled={false}
              crossReferencesEnabled={false}
              imagesEnabled={false}
              attachmentsEnabled={false}
              excalidrawEnabled={false}
            />
          </Box>
          <Stack direction='row' justifyContent='flex-end' alignItems='center' gap={1}>
            <Button variant='text' color='primary' size='small' onClick={() => { setNewItemDraft(null); setNewItemHTML(''); }} disabled={busy}>
              {t('awb.complianceChecklist.cancel')}
            </Button>
            <Button
              variant='contained'
              color='primary'
              size='small'
              onClick={handleSaveNewItem}
              disabled={busy || isCreatingItem || isNewItemEmpty}
              loading={isCreatingItem}
              loadingPosition='end'
            >
              {t('awb.complianceChecklist.save')}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Stack direction='row' justifyContent='flex-end' sx={{ pt: 6 }}>
          <Button variant='outlined' color='neutral' size='small' startIcon={<Add />} onClick={handleAddItem} disabled={busy} loading={busy} loadingPosition='end'>
            {t('awb.complianceChecklist.lexipolTemplate.addItem')}
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default ComplianceChecklist;
